// QueueCard.jsx
// One component, three variants:
//   - "active"  → drawing or cooking card (timer, ref image, slot color)
//   - "done"    → finished pancake card (cutout image, draw/cook/total time)
//   - "compact" → simple list-card for non-Main scenes (StartingSoon/Break/Outro)
//
// All three variants render at a fixed cardH so layout stacks cleanly.
// Slot styling for "active" variant comes from `slot` prop ("drawing" | "cooking").
import { memo, useEffect, useRef, useState } from "react";
import { fmtMs, typeLabel } from "./../../utils/overlayFormat";
import { computeDisplayTitle } from "./../../utils/meditations";

// Pan flag order: magenta (top) → yellow (middle) → cyan (bottom)
const SLOTS = {
  drawing: {
    bg: "linear-gradient(135deg, rgba(var(--jop-slot-drawing-rgb), 0.95) 0%, rgba(var(--jop-slot-drawing-deep-rgb), 0.95) 100%)",
    bgFlat: "rgba(var(--jop-slot-drawing-rgb), 0.95)",
    border: "rgba(var(--jop-slot-drawing-rgb), 0.55)",
    text: "white",
    dim: "rgba(255,255,255,0.6)",
    label: "✏️ Now Drawing",
    timerColor: "rgba(255,255,255,0.95)",
  },
  cooking: {
    bg: "linear-gradient(135deg, rgba(var(--jop-slot-cooking-rgb), 0.95) 0%, rgba(var(--jop-slot-cooking-deep-rgb), 0.95) 100%)",
    bgFlat: "rgba(var(--jop-slot-cooking-rgb), 0.95)",
    border: "rgba(var(--jop-slot-cooking-rgb), 0.55)",
    text: "#1a1000",
    dim: "rgba(0,0,0,0.4)",
    label: "🔥 Now Cooking",
    timerColor: "#1a1000",
  },
  done: {
    bg: "linear-gradient(135deg, rgba(var(--jop-slot-done-rgb), 0.95) 0%, rgba(var(--jop-slot-done-deep-rgb), 0.95) 100%)",
    bgFlat: "rgba(var(--jop-slot-done-rgb), 0.95)",
    border: "rgba(var(--jop-slot-done-rgb), 0.55)",
    text: "white",
    dim: "rgba(255,255,255,0.5)",
    label: "✓ Just Finished",
    timerColor: "rgba(255,255,255,0.95)",
  },
};

// ── REFERENCE IMAGE TILE (active variant) ────────────────────────────────────
function RefImage({ src, mirrored, isFreestyle, freestyleText, sizePx, textColor }) {
  const [visible, setVisible] = useState(false);
  const prevSrcRef = useRef(null);
  useEffect(() => {
    if (src !== prevSrcRef.current) {
      setVisible(false);
      const t = setTimeout(() => { setVisible(true); prevSrcRef.current = src; }, 80);
      return () => clearTimeout(t);
    }
    setVisible(true);
  }, [src]);

  const base = {
    width: `${sizePx}px`,
    height: `${sizePx}px`,
    borderRadius: "10px",
    flexShrink: 0,
    alignSelf: "center",
    opacity: visible ? 1 : 0,
    transition: "opacity 0.4s ease",
  };

  if (isFreestyle) {
    return (
      <div style={{
        ...base,
        background: "rgba(0,0,0,0.2)",
        border: "1px solid rgba(255,255,255,0.25)",
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        padding: "10px", boxSizing: "border-box",
      }}>
        <div style={{
          color: textColor === "white" ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.5)",
          fontSize: "0.85rem", textTransform: "uppercase", letterSpacing: "0.12em", marginBottom: "5px",
        }}>Freestyle</div>
        {freestyleText === null ? (
          // null = deliberately withheld (unvetted Up Next) — a glyph, not words.
          <div style={{ fontSize: "2.6rem", lineHeight: 1 }}>🎨</div>
        ) : (
          <div style={{
            color: textColor, fontSize: "1.35rem", textAlign: "center", lineHeight: 1.35, fontStyle: "italic",
          }}>"{freestyleText || "No description"}"</div>
        )}
      </div>
    );
  }

  if (!src) return <div style={{ ...base, background: "rgba(0,0,0,0.15)", opacity: 0.3 }} />;

  return (
    <div style={{ ...base, overflow: "hidden" }}>
      <img
        src={src}
        alt=""
        style={{
          width: "100%", height: "100%", objectFit: "contain",
          transform: mirrored ? "scaleX(-1)" : "none",
          transition: "transform 0.5s ease", display: "block",
        }}
      />
    </div>
  );
}

// ── ACTIVE VARIANT — drawing or cooking ──────────────────────────────────────
function ActiveCard({ order, slot, timer, timerLabel, mirrored, thumbPx, cardH, imgSrc, label: labelOverride, unvetted }) {
  const { bg, border, text, dim, label: slotLabel, timerColor } = SLOTS[slot];
  const label = labelOverride || slotLabel;
  const name = computeDisplayTitle(order) || order.subjectName || order.recipientName || order.customerName || "";

  return (
    <div style={{
      height: `${cardH}px`, background: bg, borderRadius: "14px",
      border: `2px solid ${border}`, padding: "12px 14px", boxSizing: "border-box",
      display: "flex", flexDirection: "row", gap: "12px", alignItems: "stretch", overflow: "hidden",
    }}>
      <RefImage
        src={imgSrc}
        mirrored={mirrored}
        isFreestyle={order.isFreestyle}
        // unvetted (Up Next): the guest's words stay off the tile until the
        // artist starts drawing — same rule as StatusRail's flashOnly slot.
        freestyleText={unvetted ? null : (order.notes || order.freestyleDescription)}
        sizePx={thumbPx}
        textColor={text}
      />
      <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "space-between", overflow: "hidden", minWidth: 0 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{
            background: "rgba(0,0,0,0.22)", color: text, fontSize: "1.15rem", fontWeight: 900,
            textTransform: "uppercase", letterSpacing: "0.1em", padding: "3px 10px",
            borderRadius: "20px", whiteSpace: "nowrap",
          }}>{label}</div>
          <div style={{ color: dim, fontSize: "1.25rem", fontWeight: 800 }}>#{order.queueNumber}</div>
        </div>
        <div style={{ overflow: "hidden" }}>
          {name && (
            <div style={{
              color: text, fontWeight: 800, fontSize: "1.8rem", lineHeight: 1.15,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              textShadow: text === "white" ? "0 1px 3px rgba(0,0,0,0.3)" : "none",
            }}>{name}</div>
          )}
          <div style={{
            color: dim, fontSize: "1.15rem", fontWeight: "bold", textTransform: "uppercase",
            letterSpacing: "0.07em", marginTop: "2px",
          }}>{typeLabel(order)}</div>
        </div>
        <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between" }}>
          {timer ? (
            <div>
              <div style={{
                color: timerColor, fontSize: "2.5rem", fontWeight: 900,
                fontVariantNumeric: "tabular-nums", lineHeight: 1,
              }}>{timer}</div>
              <div style={{
                color: dim, fontSize: "1.1rem", textTransform: "uppercase",
                letterSpacing: "0.06em", marginTop: "1px",
              }}>{timerLabel}</div>
            </div>
          ) : <div />}
        </div>
      </div>
    </div>
  );
}

// ── DONE VARIANT — finished pancake with stats ───────────────────────────────
function DoneCard({ order, thumbPx, cardH }) {
  const [visible, setVisible] = useState(false);
  const prevIdRef = useRef(null);
  useEffect(() => {
    if (!order) return;
    if (order.id !== prevIdRef.current) {
      setVisible(false);
      const t = setTimeout(() => { setVisible(true); prevIdRef.current = order.id; }, 80);
      return () => clearTimeout(t);
    }
    setVisible(true);
  }, [order?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const { bg, border, text, dim, label } = SLOTS.done;
  const src = order.cutoutThumbnailUrl || order.photo_cutout_url || order.thumbnailUrl || order.photo_url || null;
  const name = computeDisplayTitle(order) || order.subjectName || order.recipientName || order.customerName || "";
  const drawTime = fmtMs(order.drawingTimeMs);
  const cookTime = fmtMs(order.cookTimeMs);
  const totalMs = (order.drawingTimeMs || 0) + (order.cookTimeMs || 0);
  const totalTime = totalMs > 0 ? fmtMs(totalMs) : null;

  return (
    <div style={{
      height: `${cardH}px`, background: bg, borderRadius: "14px",
      border: `2px solid ${border}`, padding: "12px 14px", boxSizing: "border-box",
      display: "flex", flexDirection: "row", gap: "12px", alignItems: "stretch", overflow: "hidden",
      opacity: visible ? 1 : 0, transition: "opacity 0.5s ease",
    }}>
      <div style={{
        width: `${thumbPx}px`, height: `${thumbPx}px`, borderRadius: "10px", overflow: "hidden",
        flexShrink: 0, alignSelf: "center",
        background: src
          ? "repeating-conic-gradient(rgba(0,0,0,0.08) 0% 25%, transparent 0% 50%) 0 0/10px 10px"
          : "rgba(0,0,0,0.1)",
      }}>
        {src && <img src={src} alt="" style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }} />}
      </div>
      <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "space-between", overflow: "hidden", minWidth: 0 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{
            background: "rgba(0,0,0,0.22)", color: text, fontSize: "1.15rem", fontWeight: 900,
            textTransform: "uppercase", letterSpacing: "0.1em", padding: "3px 10px", borderRadius: "20px",
          }}>{label}</div>
          <div style={{ color: dim, fontSize: "1.25rem", fontWeight: 800 }}>#{order.queueNumber}</div>
        </div>
        <div style={{ overflow: "hidden", minHeight: 0 }}>
          {name && (
            <div style={{
              color: text, fontWeight: 800, fontSize: "1.8rem", lineHeight: 1.15,
              overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
              maxWidth: "100%",
            }}>{name}</div>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: "0.5rem" }}>
          <div style={{ display: "flex", gap: "0.75rem", minWidth: 0, flex: 1 }}>
            {drawTime && <span style={{ color: dim, fontSize: "1.15rem", whiteSpace: "nowrap" }}>✏️ {drawTime}</span>}
            {cookTime && <span style={{ color: dim, fontSize: "1.15rem", whiteSpace: "nowrap" }}>🔥 {cookTime}</span>}
          </div>
          {totalTime && (
            <div style={{ textAlign: "right", flexShrink: 0 }}>
              <div style={{
                color: text, fontSize: "2.5rem", fontWeight: 900,
                fontVariantNumeric: "tabular-nums", lineHeight: 1,
              }}>{totalTime}</div>
              <div style={{
                color: dim, fontSize: "1.1rem", textTransform: "uppercase",
                letterSpacing: "0.06em", marginTop: "1px",
              }}>total</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── COMPACT VARIANT — simple list-card for non-Main scenes ───────────────────
// Used by StartingSoon, Break, Outro queue lists. Just queue number + name +
// type label. No image, no timer.
function CompactCard({ order }) {
  const name = computeDisplayTitle(order) || order.subjectName || order.recipientName || order.customerName || "";
  return (
    <div style={{
      background: "rgba(255,255,255,0.08)",
      border: "1px solid rgba(255,255,255,0.15)",
      borderRadius: "10px",
      padding: "10px 14px",
      display: "flex", alignItems: "center", gap: "12px",
    }}>
      <div style={{
        background: "var(--jop-gold)", color: "#0d1b2a",
        fontWeight: 900, fontSize: "1.2rem",
        width: "42px", height: "42px", borderRadius: "50%",
        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
      }}>#{order.queueNumber}</div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          color: "white", fontWeight: 700, fontSize: "1.25rem",
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
        }}>{name || "—"}</div>
        <div style={{
          color: "rgba(255,255,255,0.55)", fontSize: "1rem",
          textTransform: "uppercase", letterSpacing: "0.06em",
        }}>{typeLabel(order)}</div>
      </div>
    </div>
  );
}

// ── DISPATCH ─────────────────────────────────────────────────────────────────
export default memo(function QueueCard({
  order,
  variant = "active",
  slot,         // "drawing" | "cooking" — only used by active variant
  timer,
  timerLabel,
  mirrored = false,
  thumbPx = 135,
  cardH = 175,
  imgSrc,
  label,        // optional header override (active variant), e.g. "⏭ Up Next"
  unvetted = false, // active variant: suppress guest-written freestyle text
}) {
  if (!order) return null;
  if (variant === "active") {
    return (
      <ActiveCard
        order={order}
        slot={slot}
        timer={timer}
        timerLabel={timerLabel}
        mirrored={mirrored}
        thumbPx={thumbPx}
        cardH={cardH}
        imgSrc={imgSrc}
        label={label}
        unvetted={unvetted}
      />
    );
  }
  if (variant === "done") {
    return <DoneCard order={order} thumbPx={thumbPx} cardH={cardH} />;
  }
  if (variant === "compact") {
    return <CompactCard order={order} />;
  }
  return null;
})
